/**
 * Omnivaso Unified Payment Gateway Abstraction Layer
 * Standardizes checkout pipelines around extensible Payment Adapters:
 * - Simulated Card Sandbox
 * - Stripe Checkout API (Credit Cards)
 * - Apple Pay (Native WebKit session + Stripe Request API)
 * - PayPal Express Checkout
 * - Square Web Payments
 */

export class BasePaymentAdapter {
    constructor(id, name, icon) {
        this.id = id;
        this.name = name;
        this.icon = icon;
    }

    getId() {
        return this.id;
    }

    getName() {
        return this.name;
    }

    getIcon() {
        return this.icon;
    }

    isAvailable(paymentInfo) {
        return true;
    }

    getType() {
        return 'card';
    }

    validate(paymentData) {
        return { valid: true };
    }

    formatPayload(orderContext, paymentData) {
        return {
            paymentMethod: this.id,
            paymentDetails: {
                gateway: this.id
            }
        };
    }
}

export class SimulatedPaymentAdapter extends BasePaymentAdapter {
    constructor() {
        super('simulated', 'Simulated Card Sandbox', '🧪');
    }

    isAvailable(paymentInfo) {
        if (paymentInfo?.enable_simulated) return true;
        if (paymentInfo?.gateway === 'simulated' || !paymentInfo?.gateway) return true;
        const hasLiveGateway = Boolean(paymentInfo?.stripe_public || paymentInfo?.square_application_id || paymentInfo?.paypal_client_id);
        return !hasLiveGateway;
    }

    getType() {
        return 'card';
    }

    validate(paymentData) {
        const { cardNumber, cardExpiry, cardCvv } = paymentData || {};
        if (!cardNumber || cardNumber.replace(/\s+/g, '').length < 13) {
            return { valid: false, error: 'Please enter a valid card number.' };
        }
        if (!cardExpiry || !cardExpiry.includes('/')) {
            return { valid: false, error: 'Please enter a valid expiry date (MM/YY).' };
        }
        if (!cardCvv || cardCvv.length < 3) {
            return { valid: false, error: 'Please enter a valid CVV.' };
        }
        return { valid: true };
    }

    formatPayload(orderContext, paymentData) {
        const cleanCard = (paymentData.cardNumber || '').replace(/\s+/g, '');
        return {
            paymentMethod: 'simulated',
            paymentDetails: {
                gateway: 'simulated',
                last4: cleanCard.slice(-4),
                expiry: paymentData.cardExpiry
            }
        };
    }
}

export class StripePaymentAdapter extends BasePaymentAdapter {
    constructor() {
        super('stripe', 'Stripe Checkout API', '💳');
    }

    isAvailable(paymentInfo) {
        if (paymentInfo?.enable_stripe === false) return false;
        const hasKey = Boolean(paymentInfo?.stripe_public);
        const isCurrent = paymentInfo?.gateway === 'stripe';
        if (paymentInfo?.hide_unconfigured !== false) {
            return isCurrent || hasKey;
        }
        return true;
    }

    getType() {
        return 'card';
    }

    validate(paymentData) {
        const { cardNumber, cardExpiry, cardCvv } = paymentData || {};
        if (!cardNumber || cardNumber.replace(/\s+/g, '').length < 14) {
            return { valid: false, error: 'Please enter a valid 16-digit credit card number.' };
        }
        if (!cardExpiry || !cardExpiry.includes('/')) {
            return { valid: false, error: 'Please enter a valid card expiration date (MM/YY).' };
        }
        if (!cardCvv || cardCvv.length < 3) {
            return { valid: false, error: 'Please enter a valid 3 or 4-digit security code (CVV).' };
        }
        return { valid: true };
    }

    formatPayload(orderContext, paymentData) {
        const cleanCard = (paymentData.cardNumber || '').replace(/\s+/g, '');
        return {
            paymentMethod: 'stripe',
            paymentDetails: {
                gateway: 'stripe',
                stripePublicKey: paymentData.stripe_public || '',
                last4: cleanCard.slice(-4),
                expiry: paymentData.cardExpiry
            }
        };
    }
}

export class ApplePayAdapter extends BasePaymentAdapter {
    constructor() {
        super('applepay', 'Apple Pay', '🍏');
    }

    isAvailable(paymentInfo) {
        const hasNativeApplePay = typeof window !== 'undefined' && window.ApplePaySession && window.ApplePaySession.canMakePayments();
        const hasStripeKey = Boolean(paymentInfo?.stripe_public);
        return hasNativeApplePay || hasStripeKey || paymentInfo?.gateway === 'stripe';
    }

    getType() {
        return 'digital-wallet';
    }

    validate(paymentData) {
        return { valid: true };
    }

    formatPayload(orderContext, paymentData) {
        return {
            paymentMethod: 'applepay',
            paymentDetails: {
                gateway: 'applepay',
                wallet: 'Apple Pay',
                authorized: true
            }
        };
    }
}

export class PayPalPaymentAdapter extends BasePaymentAdapter {
    constructor() {
        super('paypal', 'PayPal Express Checkout', '🅿️');
    }

    isAvailable(paymentInfo) {
        if (paymentInfo?.enable_paypal === false) return false;
        const hasKey = Boolean(paymentInfo?.paypal_client_id);
        const isCurrent = paymentInfo?.gateway === 'paypal';
        if (paymentInfo?.hide_unconfigured !== false) {
            return isCurrent || hasKey;
        }
        return true;
    }

    getType() {
        return 'express-button';
    }

    validate(paymentData) {
        return { valid: true };
    }

    formatPayload(orderContext, paymentData) {
        return {
            paymentMethod: 'paypal',
            paymentDetails: {
                gateway: 'paypal',
                paypalClientId: paymentData.paypal_client_id || '',
                environment: paymentData.paypal_environment || 'sandbox'
            }
        };
    }
}

export class SquarePaymentAdapter extends BasePaymentAdapter {
    constructor() {
        super('square', 'Square Web Payments', '⬛');
    }

    isAvailable(paymentInfo) {
        if (paymentInfo?.enable_square === false) return false;
        const hasKey = Boolean(paymentInfo?.square_application_id);
        const isCurrent = paymentInfo?.gateway === 'square';
        if (paymentInfo?.hide_unconfigured !== false) {
            return isCurrent || hasKey;
        }
        return true;
    }

    getType() {
        return 'card';
    }

    validate(paymentData) {
        const { cardNumber, cardExpiry, cardCvv } = paymentData || {};
        if (!cardNumber || cardNumber.replace(/\s+/g, '').length < 13) {
            return { valid: false, error: 'Please enter a valid Square card number.' };
        }
        if (!cardExpiry || !cardExpiry.includes('/')) {
            return { valid: false, error: 'Please enter a valid expiry date (MM/YY).' };
        }
        if (!cardCvv || cardCvv.length < 3) {
            return { valid: false, error: 'Please enter a valid CVV code.' };
        }
        return { valid: true };
    }

    formatPayload(orderContext, paymentData) {
        const cleanCard = (paymentData.cardNumber || '').replace(/\s+/g, '');
        return {
            paymentMethod: 'square',
            paymentDetails: {
                gateway: 'square',
                locationId: paymentData.square_location_id || '',
                last4: cleanCard.slice(-4)
            }
        };
    }
}

export class PaymentAdapterRegistry {
    constructor() {
        this.adapters = new Map();
        this.registerDefaultAdapters();
    }

    registerDefaultAdapters() {
        this.register(new SimulatedPaymentAdapter());
        this.register(new StripePaymentAdapter());
        this.register(new ApplePayAdapter());
        this.register(new PayPalPaymentAdapter());
        this.register(new SquarePaymentAdapter());
    }

    register(adapter) {
        if (adapter && adapter.getId) {
            this.adapters.set(adapter.getId(), adapter);
        }
    }

    getAdapter(id) {
        return this.adapters.get(id) || this.adapters.get('simulated');
    }

    getAvailableAdapters(paymentInfo) {
        const activeGatewayId = paymentInfo?.gateway || 'simulated';
        const list = Array.from(this.adapters.values()).filter(adapter => adapter.isAvailable(paymentInfo));

        return list.sort((a, b) => {
            if (a.getId() === activeGatewayId) return -1;
            if (b.getId() === activeGatewayId) return 1;
            return 0;
        });
    }
}

export const paymentRegistry = new PaymentAdapterRegistry();
